import { ApolloError } from "@apollo/client";
import { Save } from "@mui/icons-material";
import { Alert, Stack, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { SwitchElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

import { ResearchTypeUpdateFormFragment } from "./fragments.generated";

type Props = {
    initialData: ResearchTypeUpdateFormFragment;
    onSubmit: (value: ResearchTypeUpdateFormInputs) => void;
    error?: ApolloError;
};

export default function ResearchTypeUpdateForm({ initialData, onSubmit, error }: Props) {
    const { t } = useTranslation("researchType");
    const {
        handleSubmit,
        register,
        setError,
        control,
        formState: { errors, isDirty, isSubmitted, isSubmitting },
    } = useForm<ResearchTypeUpdateFormInputs>({
        defaultValues: {
            name: initialData.name,
            description: initialData.description,
            requireRecordings: initialData.requireRecordings,
        },
    });

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                <TextField
                    {...register("name")}
                    label={t("researchTypeUpdateForm.labelName")}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                />

                <TextField
                    {...register("description")}
                    label={t("researchTypeUpdateForm.labelDescription")}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    minRows={5}
                    multiline
                />
                <SwitchElement
                    name="requireRecordings"
                    label={t("researchTypeCreateForm.labelRequireRecordings")}
                    control={control}
                />
                <GaiaHookFormSubmitButton control={control} startIcon={<Save />}>
                    {t("researchTypeUpdateForm.btnSubmit")}
                </GaiaHookFormSubmitButton>
            </Stack>
        </form>
    );
}

export type ResearchTypeUpdateFormInputs = {
    name: string;
    description?: string | null | undefined;
    requireRecordings: boolean;
};
